import { Link, useParams } from "react-router-dom";

import { linkTo } from "../api/links";
import { useAcks, useBatches } from "../api/queries";
import { AckTable } from "../components/AckTable";
import { BatchTable } from "../components/BatchTable";
import { LoadMore } from "../components/MasterDetail";
import { ErrorBox, Guid, Panel, Spinner } from "../components/primitives";

/** One device: what it acked, and what it sent. No app version — the engine stores none. */
export function DeviceDetail() {
  const { deviceGuid = "" } = useParams();
  const acks = useAcks({ device_guid: deviceGuid });
  const batches = useBatches({ device_guid: deviceGuid });
  const ackRows = acks.data?.pages.flatMap((page) => page.acks) ?? [];
  const batchRows = batches.data?.pages.flatMap((page) => page.batches) ?? [];
  const drivers = [...new Set(ackRows.map((ack) => ack.driver_guid))];

  return (
    <div className="h-full space-y-4 overflow-y-auto p-5">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <span className="label">device</span>
        <h1 className="mono text-base text-strong">{deviceGuid}</h1>
        <Link to={linkTo.deviceBatches(deviceGuid)} className="ml-auto label hover:text-strong">
          all batches →
        </Link>
      </div>

      {drivers.length > 0 && (
        <div className="flex flex-wrap items-center gap-3">
          <span className="label">acked for</span>
          {drivers.map((driver) => (
            <Guid key={driver} value={driver} to={linkTo.driver(driver)} />
          ))}
        </div>
      )}

      <Panel
        id="device-acks"
        title={`Applied loads (${ackRows.length}${acks.hasNextPage ? "+" : ""})`}
      >
        {acks.isPending && <Spinner />}
        {acks.error && <ErrorBox error={acks.error} />}
        {acks.isSuccess && (
          <AckTable
            acks={ackRows}
            omit={["device"]}
            empty="This device has acked nothing — it has never applied a page."
          />
        )}
        <LoadMore
          hasNext={acks.hasNextPage}
          loading={acks.isFetchingNextPage}
          onClick={() => void acks.fetchNextPage()}
        />
      </Panel>

      <Panel
        id="device-batches"
        title={`Batches sent (${batchRows.length}${batches.hasNextPage ? "+" : ""})`}
      >
        {batches.isPending && <Spinner />}
        {batches.error && <ErrorBox error={batches.error} />}
        {batches.isSuccess && (
          <BatchTable
            batches={batchRows}
            omit={["device"]}
            empty="Nothing from this device is in the inbox."
          />
        )}
        <LoadMore
          hasNext={batches.hasNextPage}
          loading={batches.isFetchingNextPage}
          onClick={() => void batches.fetchNextPage()}
        />
      </Panel>
    </div>
  );
}
